import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useAxios from "../utils/useAxios";
import Swal from "sweetalert2";
import { useSidebar } from "../context/SidebarContext";
import styles from "../styles/EmployeePermissionsPage";

const EmployeePermissionsPage = () => {
  const { id } = useParams();
  const api = useAxios();
  const { sidebarVisible } = useSidebar();

  const [employee, setEmployee] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [employeePermissionIds, setEmployeePermissionIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);

  const getErrorMessage = (error, defaultMsg = "Une erreur est survenue") => {
    if (error.response?.data?.detail) return error.response.data.detail;
    if (typeof error.response?.data === "string") return error.response.data;
    return defaultMsg;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Charger infos employé
        const empRes = await api.get(`/employes/${id}/`);
        
        setEmployee(empRes.data);

        //  Charger toutes les permissions
        const permsRes = await api.get(`/permissions/`);
        
        setPermissions(permsRes.data);

        //  Charger les permissions attribuées à l’employé
        const empPermsRes = await api.get(`/permissions/employe/${id}/`);
      

        // Récupérer uniquement les IDs des permissions attribuées
        const empPermIds = empPermsRes.data.employee_permissions;
        setEmployeePermissionIds(empPermIds);

      } catch (error) {
        Swal.fire(
          "Erreur",
          getErrorMessage(error, "Impossible de charger les données"),
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);


  const handleCheckboxChange = (permId) => {
    setEmployeePermissionIds((prev) =>
      prev.includes(permId) ? prev.filter((id) => id !== permId) : [...prev, permId]
    );
  };

  const allSelected =
    permissions.length > 0 &&
    permissions.every((perm) => employeePermissionIds.includes(perm.id));

  const handleSelectAll = () => {
    if (allSelected) {
      setEmployeePermissionIds([]);
    } else {
      setEmployeePermissionIds(permissions.map((perm) => perm.id));
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      await api.post(`/permissions/employe/${id}/update/`, {
        permissions: employeePermissionIds,
      });
      Swal.fire("Succès", "Permissions mises à jour avec succès", "success");
    } catch (error) {
      Swal.fire(
        "Erreur",
        getErrorMessage(error, "Impossible de mettre à jour les permissions"),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!employee) {
    return (
      <div style={{ ...styles.container, marginLeft: sidebarVisible ? 250 : 0 }}>
        <p>Chargement des informations...</p>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, marginLeft: sidebarVisible ? 250 : 0 }}>
      <h3 style={styles.header}>
        Gérer les permissions de {employee.first_name} {employee.last_name}
      </h3>
      <hr style={styles.hr} />

      <div
        className="card shadow-sm p-4"
        style={{ borderRadius: "15px", border: "none" }}
      >
        {permissions.length > 0 ? (
          <>
            <div
              style={{
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAll}
              />
              <label style={{ fontWeight: "bold", cursor: "pointer" }}>
                Sélectionner tout
              </label>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "15px",
              }}
            >
              {permissions.map((perm) => (
                <label
                  key={perm.id}
                  onMouseEnter={() => setHoveredId(perm.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px",
                    borderRadius: "12px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    backgroundColor:
                      hoveredId === perm.id ? "#e6f0ff" : "#f9f9f9",
                    border:
                      hoveredId === perm.id
                        ? "1px solid #1B019B"
                        : "1px solid #ddd",
                    boxShadow:
                      hoveredId === perm.id
                        ? "0 4px 10px rgba(27,1,155,0.2)"
                        : "none",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={employeePermissionIds.includes(perm.id)}
                    onChange={() => handleCheckboxChange(perm.id)}
                  />
                  <span>
                    <strong>{perm.code}</strong>
                    {perm.description && ` - ${perm.description}`}
                  </span>
                </label>
              ))}
            </div>
          </>
        ) : (
          <p
            style={{ textAlign: "center", color: "#666", marginTop: "20px" }}
          >
            Aucune permission disponible.
          </p>
        )}

        <div className="text-center mt-4">
          <button
            style={{
              background: "linear-gradient(90deg, #1B019B, #3b3bff)",
              color: "#fff",
              border: "none",
              padding: "12px 25px",
              borderRadius: "25px",
              fontWeight: "bold",
              cursor: loading ? "not-allowed" : "pointer",
              minWidth: "220px",
              boxShadow: "0 4px 10px rgba(27,1,155,0.3)",
              transition: "all 0.2s",
            }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeePermissionsPage;
